/**
 * publicFormShared — pure helpers + types shared by the two public form
 * surfaces at /f/:token: the one-question-per-screen stepper (landing forms,
 * PublicForm.tsx) and the full scrollable page (study questionnaires,
 * QuestionnaireForm.tsx).
 *
 * Kept dependency-free (no React) so both surfaces evaluate show_if branching
 * and resume state identically — the visibility/equality semantics MUST match
 * the server's crmForms.js (looseEq / isQuestionVisible) verbatim.
 */

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
/** Local resume cache TTL — WCR parity (24h). */
export const LOCAL_TTL_MS = 24 * 60 * 60 * 1000;

export interface PublicQuestion {
  key: string;
  type: string;
  label: string;
  required: boolean;
  description?: string | null;
  placeholder?: string | null;
  options?: { value: string; label: string }[] | null;
  min?: number | null;
  max?: number | null;
  show_if?: { question_key: string; operator: string; value: any } | null;
}

/** WCR stored every widget answer as a string; our renderer emits real
 * numbers. equals/not_equals therefore compare with string-coercion tolerance
 * so imported conditions like equals "5" keep matching. Mirrors the server's
 * looseEq in crmForms.js — the two MUST stay in sync. */
export const looseEq = (a: any, b: any) => a === b || String(a) === String(b);

/** WCR visibleQuestions semantics: hidden while the referenced answer is
 * unanswered. null = locally CLEARED (the server deletes the row), so it
 * counts as unanswered too — otherwise the client would evaluate
 * Number(null)=0 against conditions the server sees as undefined. */
export function isVisible(q: PublicQuestion, values: Record<string, any>): boolean {
  const cond = q.show_if;
  if (!cond) return true;
  const answer = values[cond.question_key];
  if (answer === undefined || answer === null) return false;
  switch (cond.operator) {
    case 'equals': return looseEq(answer, cond.value);
    case 'not_equals': return !looseEq(answer, cond.value);
    case 'contains':
      // Verbatim WCR: arrays comma-join through String(), so substring
      // conditions on multiple_choice answers match like the old site.
      return String(answer).includes(String(cond.value));
    case 'greater_than': return Number(answer) > Number(cond.value);
    case 'less_than': return Number(answer) < Number(cond.value);
    default: return false;
  }
}

export const hasValue = (v: any) =>
  v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);

export function loadLocal(token: string): Record<string, any> {
  try {
    const raw = window.localStorage.getItem(`crm-f-${token}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Date.now() - (parsed.at || 0) > LOCAL_TTL_MS) return {};
    return parsed.answers && typeof parsed.answers === 'object' ? parsed.answers : {};
  } catch {
    return {};
  }
}

export function saveLocal(token: string, answers: Record<string, any>) {
  try {
    window.localStorage.setItem(`crm-f-${token}`, JSON.stringify({ at: Date.now(), answers }));
  } catch {
    // Private mode / quota — resume just degrades to the server's answered keys.
  }
}

export function clearLocal(token: string) {
  try {
    window.localStorage.removeItem(`crm-f-${token}`);
  } catch {
    /* noop */
  }
}
